import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { addDays, format, isToday, parseISO, subDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, Loader2, Plus, ArrowLeft,
  FileText, Download, AlertCircle, Clock, ChevronDown,
  Cloud, Droplets, Wind, Thermometer, Trash2, AlertTriangle, HardHat,
  Camera, CalendarClock, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { compressImage } from '../../lib/imageCompress';
import { DiaryLayout } from './DiaryLayout';
import { EntryDialog } from './EntryDialog';
import { AuthThumb } from './AuthThumb';
import { DiaryPhotoLightbox, entryPhotoItems } from './DiaryPhotoLightbox';
import { PAPER, TYPE_META, QUICK_ADD_TYPES, typeMeta, projectColour } from './theme';
import { CostTotalsTiles } from './CostTotalsTiles';
import { ClientPoTile } from './ClientPoTile';
import { DiaryTodos } from './DiaryTodos';
import { BCStageTracker } from '../../components/BCStageTracker';
import { ScheduleCheckBanner } from './ScheduleCheckBanner';
import { CaptureEntryCard } from './CaptureEntryCard';
import { BCEntryCard } from './BCEntryCard';

// SD-2 §4 — colour tokens shared by the intent chip on materials entries.
const INTENT_STYLES = {
  quote: { label: 'Enquiry', bg: '#FFEDD5', fg: '#9A3412', border: '#FB923C' },
  order: { label: 'Order',   bg: '#DCFCE7', fg: '#166534', border: '#16A34A' },
};
const intentStyle = (intent) => INTENT_STYLES[intent] || INTENT_STYLES.quote;

/**
 * DayView — /diary/:projectId
 *
 * Warm-paper theme with icon-forward entry types. Structure top→bottom:
 *   • Back to projects
 *   • Project banner with export button
 *   • Open items chase list (RFIs + Instructions still open) — Phase 3
 *   • Date navigation
 *   • Quick-add grid (8 typed tiles)
 *   • Timeline of today's entries
 *   • Sticky footer with primary CTA
 */
export const DayView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, isAdmin, user } = useAuth();

  // DIA-2 §2.5 — preserve /portal/diary vs /diary prefix on all in-diary
  // navigations (back-to-picker, redirects on 403, Capture return URL).
  const diaryPrefix = location.pathname.startsWith('/portal/diary') ? '/portal/diary' : '/diary';

  const [project, setProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [entries, setEntries] = useState([]);
  const [openItems, setOpenItems] = useState([]);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogType, setDialogType] = useState(null);
  // DIA-1 §2.2–§2.3 — entry currently being edited (or viewed read-only).
  // When set, opens the EntryDialog in edit mode pre-populated with this entry.
  const [editingEntry, setEditingEntry] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showOpenItems, setShowOpenItems] = useState(false);
  // SD-2 §2 — desktop admin accordion. On mobile the click is a no-op so
  // the summary card behaves exactly as it did before.
  const [expandedEntryId, setExpandedEntryId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { entry, type }
  const [deleting, setDeleting] = useState(false);
  // Bumped whenever an entry is saved / edited / deleted so the cost tiles
  // re-fetch their day/to-date totals for the currently-viewed date.
  const [costRefresh, setCostRefresh] = useState(0);
  // Plan-vs-actual schedule nudge dismissals, keyed `${projectId}:${dateKey}`.
  // Session-only (Set in state) so a dismissed nudge stays hidden while the
  // user navigates but reappears on a fresh visit if still unresolved.
  const [dismissedSchedChecks, setDismissedSchedChecks] = useState(() => new Set());
  // People to pre-select when opening a labour entry from the schedule nudge
  // ("Log these people"). Cleared when the dialog closes / saves.
  const [labourSeed, setLabourSeed] = useState(null);
  // One-tap site photo: hidden camera input + "Saving 2/3…" state on the button.
  const photoInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(null);
  // Full-screen photo viewer: { items, index, title, canDelete } or null.
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (entry, index) => {
    const items = entryPhotoItems(entry.id, entry.photos);
    if (items.length) {
      setLightbox({
        items,
        index: Math.min(index, items.length - 1),
        title: entry.title,
        canDelete: isAdmin || entry.author_id === user?.id,
      });
    }
  };
  // Bin in the viewer: off the entry AND the file to the OneDrive recycle bin.
  const handleDeletePhoto = async (item) => {
    try {
      const r = await api.delete(`/diary/entries/${item.entryId}/photos/${item.photoId}?delete_file=true`);
      toast.success(r.data?.file_deleted ? 'Photo removed' : 'Photo removed from the entry (file kept on OneDrive)');
      setLightbox(prev => {
        if (!prev) return prev;
        const items = prev.items.filter(i => i.key !== item.key);
        return items.length ? { ...prev, items, index: Math.min(prev.index, items.length - 1) } : null;
      });
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove the photo');
      throw e;
    }
  };

  // Plan-vs-actual "Log in diary" shortcut: the reconcile table can navigate
  // here with a seed date + crew to pre-fill. Consume once on mount, then
  // clear the history state so a refresh/back doesn't re-trigger it.
  useEffect(() => {
    const st = location.state;
    if (st?.seedDate) setSelectedDate(new Date(st.seedDate + 'T00:00:00'));
    if (st?.labourSeed) { setLabourSeed(st.labourSeed); setDialogType('labour'); }
    if (st?.seedDate || st?.labourSeed) window.history.replaceState({}, document.title);
    // "Back to Site Diary" from the schedule returns to the exact day: ?date=YYYY-MM-DD
    const qd = new URLSearchParams(location.search).get('date');
    if (qd && /^\d{4}-\d{2}-\d{2}$/.test(qd)) setSelectedDate(new Date(qd + 'T00:00:00'));
    // "+ New → New diary entry" from the top bar: ?new=note opens the note dialog straight away
    if (new URLSearchParams(location.search).get('new') === 'note') {
      setDialogType('note');
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const canExpand = isDesktop && isAdmin;
  const isSurveyor = user?.user_type === 'surveyor' || (user?.roles || []).includes('surveyor');
  const bcCanSend = isAdmin || isSurveyor;
  const [showBcTracker, setShowBcTracker] = useState(() => {
    try { return localStorage.getItem('lux_bc_tracker_open') === '1'; } catch { return false; }
  });

  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const displayDate = format(selectedDate, 'EEEE d MMMM yyyy', { locale: enGB });
  const colour = projectColour(project);

  const fetchProject = useCallback(async () => {
    try {
      const res = await api.get('/diary/projects');
      const proj = res.data.find(p => p.id === projectId);
      if (!proj) {
        toast.error("You don't have access to this project's diary");
        navigate(diaryPrefix);
        return;
      }
      setProject(proj);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load project');
      navigate(diaryPrefix);
    }
  }, [api, projectId, navigate, diaryPrefix]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/diary/entries?project_id=${projectId}&date=${dateKey}`);
      setEntries(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [api, projectId, dateKey]);

  // Open RFIs + Instructions (project-wide). Refetched with the timeline.
  const fetchOpenItems = useCallback(async () => {
    try {
      const res = await api.get(`/diary/open-items?project_id=${projectId}`);
      setOpenItems(res.data);
    } catch (_e) { /* non-blocking */ }
  }, [api, projectId]);

  // Auto-fill weather for the selected day (Open-Meteo via backend).
  // Silent failure — the card just doesn't render if unavailable.
  const fetchWeather = useCallback(async () => {
    try {
      const res = await api.get(`/diary/weather?project_id=${projectId}&date=${dateKey}`);
      setWeather(res.data);
    } catch (_e) {
      setWeather(null);
    }
  }, [api, projectId, dateKey]);

  // PDF export — defaults to the last 30 days ending today.
  const handleExport = async () => {
    setExporting(true);
    try {
      const end = format(new Date(), 'yyyy-MM-dd');
      const start = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const res = await api.get(`/diary/export.pdf?project_id=${projectId}&start_date=${start}&end_date=${end}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Site-Diary_${project?.project_ref || 'project'}_${start}_to_${end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetchProject(); }, [fetchProject]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchOpenItems(); }, [fetchOpenItems]);
  useEffect(() => { fetchWeather(); }, [fetchWeather]);

  // Schedule for THIS job on the viewed week — admins get the Job Schedule,
  // surveyors their Team Schedule; both read ?project=&date=. `back` brings
  // the user straight back to this day once they've made their change.
  const openSchedule = () => {
    const back = encodeURIComponent(`${location.pathname}?date=${dateKey}`);
    const q = `project=${projectId}&date=${dateKey}&back=${back}`;
    navigate(isAdmin ? `/schedule?${q}` : `/portal/team-schedule?${q}`);
  };

  // Shutter → save. Each shot is compressed and filed straight into the job's
  // Site Photos/DD-MM-YYYY folder and appended to today's "Site photos" entry.
  const handleSitePhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && f.type?.startsWith('image/'));
    if (!files.length) return;
    let saved = 0;
    try {
      for (let i = 0; i < files.length; i += 1) {
        setPhotoBusy(files.length > 1 ? `Saving ${i + 1}/${files.length}…` : 'Saving photo…');
        const compressed = await compressImage(files[i]);
        const fd = new FormData();
        fd.append('file', compressed, compressed.name);
        fd.append('date', dateKey);
        await api.post(`/diary/projects/${projectId}/site-photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        saved += 1;
      }
      toast.success(saved === 1 ? 'Photo saved to Site Photos' : `${saved} photos saved to Site Photos`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo could not be saved');
    } finally {
      setPhotoBusy(null);
      if (photoInputRef.current) photoInputRef.current.value = '';
      if (saved) fetchEntries();
    }
  };

  const quickAddTiles = useMemo(
    () => QUICK_ADD_TYPES.map(t => [t, TYPE_META[t]]).filter(([, m]) => m),
    []
  );

  const handleEntrySaved = (opts) => {
    // Materials flow: on the intermediate "email ready" view we refresh
    // the timeline silently so the entry appears at once, but keep the
    // dialog open so the user can hit "Open in Mail".
    if (!opts?.silent) setDialogType(null);
    setLabourSeed(null);
    fetchEntries();
    fetchOpenItems();
    setCostRefresh(n => n + 1);
  };

  const toggleItemStatus = async (item) => {
    const nextStatus = item.status === 'open' ? 'closed' : 'open';
    try {
      await api.patch(`/diary/entries/${item.id}`, { status: nextStatus });
      toast.success(nextStatus === 'closed' ? 'Marked closed' : 'Reopened');
      fetchOpenItems();
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  // DIA-1 §2.1 — delete + edit permissions match the backend rules exactly:
  //   • Admin can always edit / delete any entry.
  //   • Author can always edit / delete their own entry (surveyor decision).
  // Anything else, the buttons don't render — and the server 403's anyway.
  const canDelete = (entry) => {
    if (!entry) return false;
    // L-2 §0.4 — capture entries can NEVER be deleted from the diary.
    if (entry.type === 'capture') return false;
    if (isAdmin) return true;
    return entry.author_id === user?.id;
  };

  const canEdit = (entry) => {
    if (!entry) return false;
    // L-2 §0.1 — capture entries are immutable from the diary UI. Add
    // a note instead; corrections happen on the ledger row.
    if (entry.type === 'capture') return false;
    // Quick site photos have nothing to edit — the photos ARE the entry.
    if (entry.type === 'photo') return false;
    if (isAdmin) return true;
    return entry.author_id === user?.id;
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/diary/entries/${pendingDelete.entry.id}`);
      toast.success('Entry deleted');
      setPendingDelete(null);
      setExpandedEntryId(null);
      fetchEntries();
      fetchOpenItems();
      setCostRefresh(n => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DiaryLayout>
      <div className="max-w-3xl mx-auto px-4 pt-4 pb-40">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(diaryPrefix)}
          className="text-[12px] flex items-center gap-1 mb-3 hover:underline"
          style={{ color: PAPER.muted }}
          data-testid="dayview-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Projects
        </button>

        {/* Project banner */}
        {project ? (
          <div
            className="rounded-xl px-4 py-3.5 flex items-center gap-3"
            style={{
              background: PAPER.card,
              border: `1px solid ${PAPER.border}`,
              borderLeft: `6px solid ${colour.hex}`,
              boxShadow: PAPER.shadow,
            }}
            data-testid="dayview-project-banner"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>Logging to</div>
              <div className="text-[16px] font-bold truncate" style={{ color: PAPER.ink }}>{project.project_ref}</div>
              {(project.town_city || project.primary_project_type) && (
                <div className="text-[12px] truncate" style={{ color: PAPER.inkSoft }}>
                  {[project.town_city, project.primary_project_type].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={openSchedule}
              className="text-[12px] px-3 py-2 rounded-md flex items-center gap-1.5 font-semibold transition-colors hover:opacity-90"
              style={{ background: PAPER.navy, color: PAPER.accent }}
              title="Open the schedule for this job and week"
              data-testid="dayview-schedule-btn"
            >
              <CalendarClock className="w-3.5 h-3.5" />
              <span>Schedule</span>
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="text-[12px] px-3 py-2 rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors hover:bg-[#FAF7F2]"
              style={{ background: PAPER.page, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
              title="Export the last 30 days to PDF"
              data-testid="dayview-export-pdf-btn"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        ) : (
          <div className="rounded-xl h-20 animate-pulse" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }} />
        )}

        {/* Cost Ledger — Labour + Material spend-to-date tiles. Material tile
            opens the project Cost Log. Admin + surveyor only (component gates). */}
        {project && (
          <CostTotalsTiles
            projectId={projectId}
            date={dateKey}
            refreshKey={costRefresh}
            onOpenLog={(key) => navigate(`${diaryPrefix}/${projectId}/costs${key === 'labour' ? '?view=labour' : ''}`)}
          />
        )}

        {/* Client PO income — orders the client has placed for this job (Construction). */}
        {project && <ClientPoTile projectId={projectId} refreshKey={costRefresh} />}

        {/* SD-SCHED — plan-vs-actual nudge (flag-only, dismissible). */}
        {project && (
          <ScheduleCheckBanner
            projectId={projectId}
            date={dateKey}
            refreshKey={costRefresh}
            dismissed={dismissedSchedChecks.has(`${projectId}:${dateKey}`)}
            onDismiss={() => setDismissedSchedChecks(prev => new Set(prev).add(`${projectId}:${dateKey}`))}
            onLogCrew={(people) => { setLabourSeed({ people }); setDialogType('labour'); }}
            onOpenSchedule={openSchedule}
          />
        )}

        {/* SD-TODO — shared project to-do list. Sits where the weather
            card used to be; weather now renders at the bottom of the page. */}
        {project && <DiaryTodos projectId={projectId} projectRef={project?.project_ref} />}

        {/* Open items — Phase 3 chase list */}
        {openItems.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowOpenItems(s => !s)}
              className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-left transition-colors hover:bg-amber-100"
              style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}
              data-testid="dayview-open-items-toggle"
            >
              <AlertCircle className="w-4 h-4" style={{ color: '#D97706' }} />
              <span className="text-[13px] font-semibold flex-1" style={{ color: '#78350F' }}>
                {openItems.length} open item{openItems.length === 1 ? '' : 's'}
              </span>
              <span className="text-[11px] font-medium flex items-center gap-1" style={{ color: '#B45309' }}>
                {showOpenItems ? 'Hide' : 'View'}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOpenItems ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {showOpenItems && (
              <div className="mt-2 space-y-1.5" data-testid="dayview-open-items-list">
                {openItems.map(item => {
                  const meta = typeMeta(item.type);
                  const Icon = meta.icon;
                  return (
                    <div
                      key={item.id}
                      className="rounded-xl px-3.5 py-2.5 flex gap-3 items-center"
                      style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: meta.bg, color: meta.fg }}
                      >
                        <Icon className="w-4 h-4" strokeWidth={2.25} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold"
                            style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}
                          >{item.reference}</span>
                          <span className="text-[13px] font-semibold truncate" style={{ color: PAPER.ink }}>{item.title}</span>
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: PAPER.muted }}>
                          {item.date === dateKey ? 'today' : format(parseISO(item.date), 'dd/MM')} · {item.author_name || 'someone'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleItemStatus(item)}
                        className="text-[11px] font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap transition-colors"
                        style={{ background: '#D1FAE5', color: '#065F46' }}
                      >
                        Close
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Date navigation */}
        <div
          className="mt-4 flex items-center justify-between rounded-xl px-3 py-2.5"
          style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
        >
          <button
            type="button"
            onClick={() => setSelectedDate(d => addDays(d, -1))}
            className="p-1.5 rounded-md hover:bg-[#FAF7F2] transition-colors"
            style={{ color: PAPER.inkSoft }}
            data-testid="dayview-prev-day-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center">
            <div className="text-[14px] font-semibold" style={{ color: PAPER.ink }}>{displayDate}</div>
            <div className="text-[11px]" style={{ color: PAPER.muted }}>{format(selectedDate, 'dd/MM/yyyy')}{isToday(selectedDate) ? ' · today' : ''}</div>
          </div>
          <button
            type="button"
            onClick={() => setSelectedDate(d => addDays(d, 1))}
            className="p-1.5 rounded-md hover:bg-[#FAF7F2] transition-colors"
            style={{ color: PAPER.inkSoft }}
            data-testid="dayview-next-day-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {!isToday(selectedDate) && (
          <button
            type="button"
            onClick={() => setSelectedDate(new Date())}
            className="mt-2 text-[12px] font-medium hover:underline"
            style={{ color: PAPER.accent }}
            data-testid="dayview-jump-today-btn"
          >
            Jump to today
          </button>
        )}

        {/* Quick-add tiles — big icon chips */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-2.5" style={{ color: PAPER.muted }}>
            Log an entry
          </div>

          {/* Fastest actions: shutter → Site Photos, and a plain note. */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleSitePhotos(e.target.files)}
            data-testid="site-photo-input"
          />
          <div className="flex gap-2.5 mb-2.5">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={!!photoBusy}
              className="flex-1 min-w-0 rounded-xl px-4 py-3 flex items-center gap-3 text-left transition-transform active:scale-[0.99] disabled:opacity-70"
              style={{ background: PAPER.navy, boxShadow: PAPER.shadow }}
              data-testid="site-photo-btn"
            >
              <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: PAPER.accent, color: PAPER.accentInk }}>
                {photoBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" strokeWidth={2.25} />}
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-bold text-white">{photoBusy || 'Site photo'}</span>
                <span className="block text-[11.5px] truncate" style={{ color: '#CBD5E1' }}>
                  {photoBusy ? 'Filing to Site Photos' : 'Camera opens · saved to Site Photos'}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setDialogType('note')}
              className="w-[92px] rounded-xl flex flex-col items-center justify-center gap-1 transition-transform active:scale-95 hover:shadow-md"
              style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }}
              title="Write a note"
              data-testid="quickadd-note-btn"
            >
              <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: TYPE_META.note.bg, color: TYPE_META.note.fg }}>
                <Plus className="w-4 h-4" strokeWidth={2.5} />
              </span>
              <span className="text-[12px] font-semibold" style={{ color: PAPER.ink }}>Note</span>
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2.5">
            {quickAddTiles.map(([type, meta]) => {
              const Icon = meta.icon;
              // DIA-1 §2.5 — Capture tile launches the existing Capture flow,
              // pre-filling and LOCKING the project, and asks it to create a
              // companion diary entry on completion.
              const diaryReturn = `${diaryPrefix}/${projectId}`;
              const onTileClick = type === 'capture'
                ? () => {
                    const params = new URLSearchParams({
                      project_id: projectId,
                      from_diary: '1',
                      locked: '1',
                      return: diaryReturn,
                    });
                    navigate(`/portal/capture?${params.toString()}`);
                  }
                : type === 'bc_capture'
                ? () => {
                    const params = new URLSearchParams({
                      project_id: projectId,
                      locked: '1',
                      return: diaryReturn,
                    });
                    navigate(`/portal/building-control?${params.toString()}`);
                  }
                : () => setDialogType(type);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={onTileClick}
                  className="aspect-square rounded-xl flex flex-col items-center justify-center gap-1.5 transition-transform active:scale-95 hover:shadow-md"
                  style={{
                    background: PAPER.card,
                    border: `1px solid ${PAPER.border}`,
                    boxShadow: PAPER.shadow,
                  }}
                  data-testid={`quickadd-${type}-btn`}
                  title={`Log a ${meta.label.toLowerCase()}`}
                >
                  <span
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: meta.bg, color: meta.fg }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                  <span className="text-[12px] font-semibold" style={{ color: PAPER.ink }}>{meta.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Building Control progress — per-project stage checklist (surveyors/admins) */}
        {bcCanSend && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => { const v = !showBcTracker; setShowBcTracker(v); try { localStorage.setItem('lux_bc_tracker_open', v ? '1' : '0'); } catch { /* ignore */ } }}
              className="w-full flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold mb-2.5"
              style={{ color: PAPER.muted }}
              data-testid="bc-tracker-toggle"
            >
              {showBcTracker ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <HardHat className="w-3.5 h-3.5" /> Building Control progress
            </button>
            {showBcTracker && <BCStageTracker projectId={projectId} variant="paper" refreshKey={entries.length} />}
          </div>
        )}

        {/* Timeline */}
        <div className="mt-7">
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-2.5" style={{ color: PAPER.muted }}>
            Today&apos;s entries {entries.length > 0 && <span style={{ color: PAPER.hint }}>· {entries.length}</span>}
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: PAPER.accent }} />
            </div>
          ) : entries.length === 0 ? (
            <div
              className="rounded-xl px-4 py-10 text-center"
              style={{
                background: PAPER.card,
                border: `1px dashed ${PAPER.border}`,
              }}
            >
              <FileText className="w-6 h-6 mx-auto mb-2" style={{ color: PAPER.hint }} />
              <div className="text-[13px]" style={{ color: PAPER.inkSoft }}>No entries yet for this day.</div>
              <div className="text-[12px] mt-1" style={{ color: PAPER.muted }}>Tap a quick-add tile above to start logging.</div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {entries.map(entry => {
                // L-2 §0.3 — capture entries render via a dedicated card
                // that shows photo / supplier / category / value (admin
                // + surveyor) / tags / notes thread + "Add note". Edit
                // and Delete deliberately absent.
                if (entry.type === 'capture') {
                  return (
                    <CaptureEntryCard
                      key={entry.id}
                      entry={entry}
                      onNoteAdded={fetchEntries}
                    />
                  );
                }
                if (entry.type === 'bc_capture') {
                  return (
                    <BCEntryCard
                      key={entry.id}
                      entry={entry}
                      canSend={bcCanSend}
                      isAdmin={isAdmin}
                      onChanged={fetchEntries}
                    />
                  );
                }
                const meta = typeMeta(entry.type);
                const Icon = meta.icon;
                const created = entry.created_at ? parseISO(entry.created_at) : null;
                const timeLabel = created ? format(created, 'HH:mm') : '';
                const summary = entrySummary(entry);
                const isMaterials = entry.type === 'materials';
                const intent = isMaterials ? (entry.payload?.intent || 'quote') : null;
                const intentSt = intent ? intentStyle(intent) : null;
                const expanded = expandedEntryId === entry.id;
                const isPhoto = entry.type === 'photo';
                const isExpandable = canExpand || isPhoto;
                // DIA-1 §2.3 — on mobile, tapping an entry opens the edit
                // sheet (or read-only view if the user isn't the author).
                // On desktop it stays as the expanded-accordion pattern.
                // Site-photo entries always expand in place (bigger thumbnails).
                const onCardClick = isExpandable
                  ? () => setExpandedEntryId(prev => prev === entry.id ? null : entry.id)
                  : () => setEditingEntry(entry);

                return (
                  <div
                    key={entry.id}
                    onClick={onCardClick}
                    className={`rounded-xl px-3.5 py-3 flex gap-3 transition-all cursor-pointer hover:shadow-md`}
                    style={{
                      background: PAPER.card,
                      border: `1px solid ${expanded ? PAPER.accent : PAPER.border}`,
                      boxShadow: PAPER.shadow,
                    }}
                    data-testid={`timeline-entry-${entry.id}`}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: (isMaterials && intentSt) ? intentSt.bg : meta.bg, color: (isMaterials && intentSt) ? intentSt.fg : meta.fg }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isMaterials && intentSt ? (
                          <span
                            className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                            style={{ background: intentSt.bg, color: intentSt.fg }}
                            data-testid={`timeline-intent-${intent}`}
                          >
                            {intentSt.label}
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.fg }}>
                            {meta.label}
                          </span>
                        )}
                        {entry.reference && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold"
                            style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}
                          >{entry.reference}</span>
                        )}
                        {entry.status === 'open' && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                            style={{ background: '#FEF3C7', color: '#92400E' }}
                          >OPEN</span>
                        )}
                        {entry.status === 'closed' && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                            style={{ background: '#D1FAE5', color: '#065F46' }}
                          >CLOSED</span>
                        )}
                        <span className="ml-auto text-[11px] flex items-center gap-1" style={{ color: PAPER.muted }}>
                          <Clock className="w-3 h-3" />{timeLabel}
                        </span>
                      </div>
                      <div className="text-[15px] font-semibold mt-0.5" style={{ color: PAPER.ink }}>
                        {entry.title}
                      </div>
                      {summary && (
                        <div className="text-[13px] mt-0.5" style={{ color: PAPER.inkSoft }}>{summary}</div>
                      )}
                      {entry.detail && (
                        <div
                          className={`text-[13px] mt-1 whitespace-pre-line leading-snug ${expanded ? '' : 'line-clamp-4'}`}
                          style={{ color: PAPER.ink }}
                        >
                          {entry.detail}
                        </div>
                      )}
                      {/* Photo thumbnails — larger grid when expanded. Tap opens
                          the full-screen viewer (swipe between shots); served
                          through the authenticated thumb proxy. */}
                      {entry.photos && entry.photos.length > 0 && (
                        <div className="mt-2 flex gap-1.5 flex-wrap" data-testid={`timeline-photos-${entry.id}`}>
                          {entry.photos.slice(0, expanded ? entry.photos.length : 6).map((photo, idx) => (
                            <button
                              key={photo.id || photo.web_url || idx}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openLightbox(entry, idx); }}
                              className={`${expanded ? 'w-24 h-24' : 'w-14 h-14'} rounded-md overflow-hidden flex-shrink-0 transition-transform active:scale-95`}
                              title={photo.name || 'View photo'}
                              data-testid={`timeline-photo-${photo.id || idx}`}
                            >
                              <AuthThumb
                                endpoint={photo.id ? `/diary/entries/${entry.id}/photos/${photo.id}/thumb` : null}
                                enabled={!!photo.id}
                                testKey={photo.id || `${entry.id}-${idx}`}
                                placeholderIcon={ImageIcon}
                                alt={photo.name || 'Site photo'}
                                className="w-full h-full rounded-md"
                              />
                            </button>
                          ))}
                          {!expanded && entry.photos.length > 6 && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openLightbox(entry, 6); }}
                              className="w-14 h-14 rounded-md flex items-center justify-center text-[11px] font-semibold"
                              style={{ background: PAPER.paperMuted, color: PAPER.inkSoft, border: `1px solid ${PAPER.border}` }}
                              data-testid={`timeline-photos-more-${entry.id}`}
                            >
                              +{entry.photos.length - 6}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Expanded detail view — SD-2 §2 */}
                      {expanded && (
                        <div
                          className="mt-3 pt-3 border-t"
                          style={{ borderColor: PAPER.divider }}
                          data-testid={`timeline-entry-expanded-${entry.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExpandedDetail entry={entry} />
                          {(canEdit(entry) || canDelete(entry)) && (
                            <div className="mt-3 flex justify-end gap-2">
                              {canEdit(entry) && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setEditingEntry(entry); }}
                                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-colors"
                                  style={{ background: PAPER.paperMuted, color: PAPER.ink, border: `1px solid ${PAPER.border}` }}
                                  data-testid={`timeline-edit-btn-${entry.id}`}
                                >
                                  Edit entry
                                </button>
                              )}
                              {canDelete(entry) && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setPendingDelete({ entry }); }}
                                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md transition-colors"
                                  style={{ background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5' }}
                                  data-testid={`timeline-delete-btn-${entry.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Delete entry
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-[11px] mt-1.5" style={{ color: PAPER.muted }}>
                        {entry.author_name || 'Someone'}
                        {entry.added_on && entry.added_on !== entry.date && (
                          <span className="ml-2 font-medium" style={{ color: '#B45309' }}>
                            added {format(parseISO(entry.added_on), 'dd/MM')} for {format(parseISO(entry.date), 'dd/MM')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Weather auto-fill card — moved to the bottom of the page (SD-TODO). */}
        {weather && weather.available && (
          <div
            className="mt-4 rounded-xl px-3.5 py-3 flex items-center gap-3"
            style={{
              background: PAPER.card,
              border: `1px solid ${PAPER.border}`,
              boxShadow: PAPER.shadow,
            }}
            data-testid="dayview-weather-card"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: '#CFFAFE', color: '#0891B2' }}
            >
              <Cloud className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
                {isToday(selectedDate) ? "Today's weather" : "Weather"} · {weather.postcode}
              </div>
              <div className="text-[14px] font-semibold" style={{ color: PAPER.ink }}>
                {weather.condition || '—'}
              </div>
              <div className="flex items-center gap-3 text-[12px] mt-0.5 flex-wrap" style={{ color: PAPER.inkSoft }}>
                {weather.temp_max != null && (
                  <span className="flex items-center gap-0.5">
                    <Thermometer className="w-3 h-3" />
                    {Math.round(weather.temp_min ?? weather.temp_max)}–{Math.round(weather.temp_max)}°C
                  </span>
                )}
                {weather.precip_probability != null && (
                  <span className="flex items-center gap-0.5">
                    <Droplets className="w-3 h-3" />
                    {weather.precip_probability}% rain
                  </span>
                )}
                {weather.wind_kmh_max != null && (
                  <span className="flex items-center gap-0.5">
                    <Wind className="w-3 h-3" />
                    {Math.round(weather.wind_kmh_max)} km/h
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky "New entry" footer */}
      <div
        className="fixed bottom-0 left-0 right-0 z-20"
        style={{
          background: 'rgba(250,247,242,0.95)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderTop: `1px solid ${PAPER.border}`,
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3">
          <button
            type="button"
            onClick={() => setDialogType('note')}
            className="w-full font-semibold text-[14px] rounded-xl py-3.5 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{ background: PAPER.navy, color: PAPER.accent }}
            data-testid="dayview-new-note-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={3} />
            New Note
          </button>
        </div>
      </div>

      {dialogType && (
        <EntryDialog
          type={dialogType}
          projectId={projectId}
          date={dateKey}
          initialPayload={dialogType === 'labour' ? labourSeed : undefined}
          onClose={() => { setDialogType(null); setLabourSeed(null); }}
          onSaved={handleEntrySaved}
        />
      )}

      {/* DIA-1 §2.2/§2.3 — edit dialog. Same modal, pre-populated with the
          entry. If the user can't edit (not the author), the dialog switches
          to a read-only view so they still see the detail. */}
      {editingEntry && (
        <EntryDialog
          entry={editingEntry}
          type={editingEntry.type}
          projectId={projectId}
          date={editingEntry.date}
          canEdit={canEdit(editingEntry)}
          canDelete={canDelete(editingEntry)}
          onClose={() => setEditingEntry(null)}
          onSaved={() => { setEditingEntry(null); fetchEntries(); fetchOpenItems(); setCostRefresh(n => n + 1); }}
          onPhotosChanged={fetchEntries}
          onDeleteRequest={(e) => { setEditingEntry(null); setPendingDelete({ entry: e }); }}
        />
      )}

      {/* SD-2 §1 — soft-delete confirmation dialog. Long wording per the
          spec so admins know that photos on OneDrive stay put and any
          email already sent cannot be recalled. */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(31,41,55,0.55)', backdropFilter: 'blur(4px)' }}
          data-testid="delete-confirm-dialog"
        >
          <div
            className="max-w-md w-full rounded-2xl overflow-hidden"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}
          >
            <div className="p-5 flex gap-3 items-start">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#FEE2E2', color: '#B91C1C' }}
              >
                <AlertTriangle className="w-5 h-5" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[16px] font-bold" style={{ color: PAPER.ink }}>
                  Delete this entry?
                </div>
                <div className="text-[13px] mt-2 leading-relaxed" style={{ color: PAPER.inkSoft }}>
                  It won&apos;t be visible in the app any more.<br />
                  <b>Photos already uploaded to OneDrive will stay in the folder</b> — remove those separately if needed.<br />
                  <b>Emails already sent to suppliers cannot be recalled.</b>
                </div>
              </div>
            </div>
            <div
              className="px-5 py-3 flex gap-2 justify-end"
              style={{ background: PAPER.page, borderTop: `1px solid ${PAPER.border}` }}
            >
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="text-[13px] font-medium rounded-lg px-4 py-2 hover:bg-[#FAF7F2] disabled:opacity-50"
                style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                data-testid="delete-cancel-btn"
              >
                Keep entry
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="text-[13px] font-semibold rounded-lg px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: '#DC2626', color: '#FFFFFF' }}
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete entry
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <DiaryPhotoLightbox
          items={lightbox.items}
          index={lightbox.index}
          title={lightbox.title}
          canDelete={lightbox.canDelete}
          onDelete={handleDeletePhoto}
          onIndexChange={(i) => setLightbox(prev => (prev ? { ...prev, index: i } : prev))}
          onClose={() => setLightbox(null)}
        />
      )}
    </DiaryLayout>
  );
};

export default DayView;

/**
 * ExpandedDetail — SD-2 §2. Rendered when a desktop admin clicks a
 * timeline entry. Focus is on materials orders (the busiest type in real
 * use) but standard diary entries also get their full detail shown here.
 */
const ExpandedDetail = ({ entry }) => {
  const p = entry.payload || {};
  const created = entry.created_at ? parseISO(entry.created_at) : null;
  if (entry.type === 'materials') {
    const items = p.line_items || [];
    return (
      <div className="space-y-3 text-[13px]" style={{ color: PAPER.ink }}>
        {/* Supplier + email sent status */}
        <DetailRow label="Supplier">
          <span>
            {p.supplier_name || <em style={{ color: PAPER.muted }}>—</em>}
            {p.supplier_email && (
              <span style={{ color: PAPER.muted }}> · {p.supplier_email}</span>
            )}
          </span>
        </DetailRow>
        {p.delivery_by && (
          <DetailRow label="Required by">{formatDeliveryBy(p.delivery_by)}</DetailRow>
        )}
        {/* Line items table */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
            Line items · {items.length}
          </div>
          <div
            className="rounded-md overflow-hidden"
            style={{ border: `1px solid ${PAPER.border}` }}
          >
            {items.map((it, idx) => (
              <div
                key={idx}
                className="px-3 py-2 flex items-start gap-2 flex-wrap"
                style={{ borderBottom: idx < items.length - 1 ? `1px solid ${PAPER.divider}` : 'none' }}
              >
                <span className="text-[10px] font-mono mt-0.5" style={{ color: PAPER.hint }}>{idx + 1}</span>
                <span className="flex-1 min-w-0">
                  <div className="font-semibold" style={{ color: PAPER.ink }}>{it.description || 'Item'}</div>
                  {(it.width_mm || it.depth_mm || it.length_m || it.size) && (
                    <div className="text-[12px]" style={{ color: PAPER.inkSoft }}>
                      {formatDim(it)}
                    </div>
                  )}
                  {it.notes && (
                    <div className="text-[12px] mt-0.5 italic" style={{ color: PAPER.muted }}>{it.notes}</div>
                  )}
                </span>
                <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: PAPER.ink }}>
                  {it.qty || '—'} {it.unit || ''}
                </span>
              </div>
            ))}
          </div>
        </div>
        {p.extras && (
          <DetailRow label="Notes for supplier">
            <span className="whitespace-pre-line">{p.extras}</span>
          </DetailRow>
        )}
        <DetailRow label="Created">
          {created ? format(created, 'EEE d MMM yyyy · HH:mm') : '—'}
          {entry.author_name && <span style={{ color: PAPER.muted }}> · by {entry.author_name}</span>}
        </DetailRow>
        <DetailRow label="Email">
          {p.email_sent_at ? (
            <span style={{ color: '#166534' }}>
              Draft prepared {format(parseISO(p.email_sent_at), 'dd MMM HH:mm')}
            </span>
          ) : (
            <span style={{ color: PAPER.muted }}>Not sent yet</span>
          )}
        </DetailRow>
      </div>
    );
  }

  // Quick site photos — when they were taken and the SharePoint folder they sit in.
  if (entry.type === 'photo') {
    const folder = (p.folder || '').split('/').slice(-2).join(' / ');
    return (
      <div className="space-y-2 text-[13px]" style={{ color: PAPER.ink }} data-testid={`photo-entry-detail-${entry.id}`}>
        <DetailRow label="Taken">
          {created ? format(created, 'EEE d MMM yyyy · HH:mm') : '—'}
          {entry.author_name && <span style={{ color: PAPER.muted }}> · by {entry.author_name}</span>}
        </DetailRow>
        {folder && (
          <DetailRow label="Filed to"><span className="font-mono text-[12px]">{folder}</span></DetailRow>
        )}
      </div>
    );
  }

  // Standard diary entries — just make sure the full detail block is visible.
  return (
    <div className="space-y-2 text-[13px]" style={{ color: PAPER.ink }}>
      <DetailRow label="Created">
        {created ? format(created, 'EEE d MMM yyyy · HH:mm') : '—'}
        {entry.author_name && <span style={{ color: PAPER.muted }}> · by {entry.author_name}</span>}
      </DetailRow>
      {entry.edit_history && entry.edit_history.length > 0 && (
        <DetailRow label="Edits">
          {entry.edit_history.length} previous version{entry.edit_history.length === 1 ? '' : 's'}
        </DetailRow>
      )}
    </div>
  );
};

const DetailRow = ({ label, children }) => (
  <div className="flex gap-2 flex-wrap">
    <span
      className="text-[10px] uppercase tracking-wider font-semibold w-24 flex-shrink-0 pt-0.5"
      style={{ color: PAPER.muted }}
    >{label}</span>
    <span className="flex-1 min-w-0 text-[13px]">{children}</span>
  </div>
);

const formatDim = (item) => {
  const parts = [];
  if (item.width_mm) parts.push(`${item.width_mm}mm`);
  if (item.depth_mm) parts.push(`${item.depth_mm}mm`);
  if (item.length_m) parts.push(`${item.length_m}m`);
  if (parts.length === 0 && item.size) return item.size; // legacy free-text
  return parts.join(' x ');
};

const formatDeliveryBy = (isoDate) => {
  try { return format(parseISO(isoDate), 'EEE d MMM yyyy'); }
  catch { return isoDate; }
};

/**
 * Short 1-line summary of the type-specific payload, rendered under the
 * title on the timeline.
 */
function entrySummary(entry) {
  const p = entry?.payload || {};
  switch (entry.type) {
    case 'labour': {
      const people = p.people || [];
      if (people.length === 0) return null;
      const names = people.slice(0, 4).map(x => x.name).join(', ');
      const more = people.length > 4 ? ` +${people.length - 4} more` : '';
      return `${people.length} on site — ${names}${more}`;
    }
    case 'delivery': {
      const parts = [p.supplier, p.materials, p.quantity].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'weather': {
      const impactLabel = { partial: 'partly', stopped: 'stopped' }[p.impact];
      const parts = [p.condition, impactLabel ? `work ${impactLabel}${p.duration ? ` (${p.duration})` : ''}` : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'delay': {
      const parts = [p.cause, p.impact && `impact: ${p.impact}`].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'rfi':
      return p.raised_with ? `Raised with ${p.raised_with}` : null;
    case 'instruction':
      return p.party ? `${p.direction === 'issued' ? 'Issued to' : 'Received from'} ${p.party}` : null;
    case 'visit':
      return p.attendees || null;
    case 'materials': {
      const intent = (p.intent || 'quote') === 'order' ? 'Order' : 'Quote';
      const supplier = p.supplier_name || 'supplier';
      const nItems = (p.line_items || []).length;
      const itemWord = nItems === 1 ? 'item' : 'items';
      return `${intent} to ${supplier} · ${nItems} ${itemWord}`;
    }
    default:
      return null;
  }
}
